import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Sparkles, Mail, CheckCircle2 } from "lucide-react";
import { useCartStore } from "@/stores/cartStore";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import type { User } from "@supabase/supabase-js";
import { z } from "zod";

interface WaitlistModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const WaitlistModal = ({ open, onOpenChange }: WaitlistModalProps) => {
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [additionalEmail, setAdditionalEmail] = useState("");
  const { items } = useCartStore();

  const totalAmount = items.reduce((sum, item) => sum + (parseFloat(item.price.amount) * item.quantity), 0);
  const currencyCode = items[0]?.price.currencyCode || 'USD';

  // Check if user is logged in and pre-fill their email
  useEffect(() => {
    const checkUser = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      setUser(user);
      if (user?.email) {
        setEmail(user.email);
      }
    };
    
    if (open) {
      checkUser();
    }
  }, [open]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate email
    const emailSchema = z.string().email("Invalid email address").max(255, "Email must be less than 255 characters");
    const emailValidation = emailSchema.safeParse(email);
    
    if (!emailValidation.success) {
      toast({
        title: "Invalid email",
        description: emailValidation.error.errors[0].message,
        variant: "destructive"
      });
      return;
    }

    setIsSubmitting(true);

    try {
      // Insert into waitlist with user_id if logged in
      const { error } = await supabase.from('waitlist_signups').insert({
        email,
        cart_items: items as any,
        total_amount: totalAmount,
        currency_code: currencyCode,
        user_id: user?.id || null
      });

      if (error) throw error;

      // Update checkout attempt to mark as converted
      await supabase.from('checkout_attempts').insert({
        cart_items: items as any,
        total_amount: totalAmount,
        currency_code: currencyCode,
        converted_to_waitlist: true
      });

      setIsSuccess(true);

    } catch (error) {
      if (import.meta.env.DEV) {
        console.error('Failed to join waitlist:', error);
      }
      toast({
        title: "Something went wrong",
        description: "Please try again later.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        {!isSuccess ? (
          <>
            <DialogHeader>
              <div className="flex items-center gap-2 mb-2">
                <Sparkles className="h-5 w-5 text-product-pink" />
                <DialogTitle className="text-2xl">Launching Soon!</DialogTitle>
              </div>
              <DialogDescription className="text-base">
                We're putting the finishing touches on our collection. Join the waitlist and be the first to know when we launch!
              </DialogDescription>
            </DialogHeader>

            <div className="bg-secondary/30 rounded-lg p-4 my-4">
              <h4 className="font-semibold mb-2">Your Cart Summary</h4>
              <div className="space-y-1 text-sm text-muted-foreground">
                {items.map((item) => (
                  <div key={item.variantId} className="flex justify-between">
                    <span>{item.product.node.title} × {item.quantity}</span>
                    <span>{currencyCode} {(parseFloat(item.price.amount) * item.quantity).toFixed(2)}</span>
                  </div>
                ))}
                <div className="flex justify-between font-semibold text-foreground pt-2 border-t">
                  <span>Total</span>
                  <span>{currencyCode} {totalAmount.toFixed(2)}</span>
                </div>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email Address</Label>
                {user && (
                  <p className="text-xs text-muted-foreground mb-1">
                    Using email from your account
                  </p>
                )}
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    disabled={!!user}
                    className="pl-10"
                  />
                </div>
              </div>

              <div className="bg-gradient-to-r from-product-pink/20 to-accent/20 border-2 border-product-pink/50 rounded-lg p-4 text-sm shadow-md">
                <p className="font-bold text-product-pink mb-1 text-base flex items-center gap-2">
                  <Sparkles className="h-4 w-4" />
                  🎁 Early Bird Bonus!
                </p>
                <p className="text-foreground font-medium">Get 50% off your first order when we launch</p>
              </div>

              <Button type="submit" className="w-full" disabled={isSubmitting}>
                {isSubmitting ? "Joining..." : "Join Waitlist"}
              </Button>
            </form>
          </>
        ) : (
          <div className="py-6">
            <div className="text-center mb-6">
              <CheckCircle2 className="h-16 w-16 text-green-500 mx-auto mb-4" />
              <DialogTitle className="text-2xl mb-2">You're on the list! 🎉</DialogTitle>
              <DialogDescription className="text-base">
                We'll send you an email with your exclusive 50% discount code when we launch.
              </DialogDescription>
            </div>
            
            <div className="border-t pt-6 space-y-4">
              <h4 className="font-semibold text-sm">Add another email to the waitlist</h4>
              <form onSubmit={async (e) => {
                e.preventDefault();
                
                const emailSchema = z.string().email("Invalid email address").max(255, "Email must be less than 255 characters");
                const emailValidation = emailSchema.safeParse(additionalEmail);
                
                if (!emailValidation.success) {
                  toast({
                    title: "Invalid email",
                    description: emailValidation.error.errors[0].message,
                    variant: "destructive"
                  });
                  return;
                }

                setIsSubmitting(true);
                
                try {
                  const { error } = await supabase.from('waitlist_signups').insert({
                    email: additionalEmail,
                    cart_items: items as any,
                    total_amount: totalAmount,
                    currency_code: currencyCode,
                    user_id: user?.id || null
                  });

                  if (error) throw error;

                  toast({
                    title: "Success!",
                    description: "Email added to the waitlist"
                  });
                  
                  setAdditionalEmail("");
                } catch (error) {
                  console.error('Failed to add email:', error);
                  toast({
                    title: "Something went wrong",
                    description: "Please try again later.",
                    variant: "destructive"
                  });
                } finally {
                  setIsSubmitting(false);
                }
              }} className="space-y-3">
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    type="email"
                    placeholder="friend@example.com"
                    value={additionalEmail}
                    onChange={(e) => setAdditionalEmail(e.target.value)}
                    required
                    className="pl-10"
                  />
                </div>
                <div className="flex gap-2">
                  <Button type="submit" className="flex-1" disabled={isSubmitting} size="sm">
                    {isSubmitting ? "Adding..." : "Add Email"}
                  </Button>
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={() => {
                      onOpenChange(false);
                      setIsSuccess(false);
                      setEmail("");
                      setAdditionalEmail("");
                    }}
                    size="sm"
                  >
                    Close
                  </Button>
                </div>
              </form>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
